'use client'
import { Otp } from '@repo/shared/components'
import { useTranslate } from '@repo/shared/hooks'
import { useRouter } from '@repo/shared/i18n/navigation'
import {
  useLoginByPhone,
  useLoginByEmail,
  useResendOtp,
  useFeatureFlags,
} from '@repo/services'
import { Button, Card, Col, Icon } from '@repo/ui/components'
import { useRef, useState } from 'react'
import { ILoginOtpProps } from './login.types'
import { normalizeMobile } from '@repo/ui/lib'
import { OTP_LENGTH } from '@repo/shared/variables/otp.var'
import { useErrorHandler } from '@repo/shared/hooks'
import { toast } from '@repo/ui/components'
import { useFeatureFlagsStore } from '@repo/shared/store'
import { EAnalyticsEventName, trackEvent } from '@repo/shared/adapters'
import { Cookie } from '@repo/tools'

export const LoginOtp = (props: ILoginOtpProps) => {
  const { identifier, handleBack, otp, isEmail, redirectingUrl } = props
  const { t } = useTranslate()
  const { replace } = useRouter()
  const [otpCode, setOtpCode] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const loginByPhone = useLoginByPhone()
  const loginByEmail = useLoginByEmail()
  const resendOtpMutation = useResendOtp()
  const { errorHandler } = useErrorHandler()
  const featureFlagsQuery = useFeatureFlags()
  const { setFlags } = useFeatureFlagsStore()

  const submitRef = useRef(null)

  const getResendOtpPayload = () =>
    isEmail
      ? { email: identifier, otp }
      : { phone: normalizeMobile(identifier), otp }

  const handleResend = () => {
    resendOtpMutation.mutate(getResendOtpPayload(), {
      onSuccess: () => {
        toast.success({
          title: t('common_otp_resend'),
        })
      },
      onError: errorHandler,
    })
  }

  const handleOtpComplete = (code: string) => {
    setOtpCode(code)
  }

  const handleEdit = () => {
    handleBack()
  }

  const handleSubmit = async (code: string = otpCode) => {
    if (!code || code.length < OTP_LENGTH) return

    setIsLoading(true)

    if (isEmail) {
      loginByEmail.mutate(
        {
          email: identifier,
          otp,
          code,
        },
        {
          onSuccess: async (res) => {
            if (res.data?.accessToken && res.data?.refreshToken) {
              // Refetch feature flags after successful login
              featureFlagsQuery.refetch()
              if (featureFlagsQuery.data?.flags) {
                setFlags(featureFlagsQuery.data.flags)
              }

              trackEvent({
                name: EAnalyticsEventName.Login,
                properties: {
                  user_id_custom: res.data.userID,
                },
              })
              // Clear redirecting URL and redirect to saved URL or dashboard
              Cookie.clearRedirectingUrl()
              const targetUrl = redirectingUrl ?? '/dashboard'
              // Use window.location.replace to redirect and clear history
              if (typeof window !== 'undefined') {
                window.location.replace(targetUrl)
              } else {
                replace(targetUrl)
              }
            }
          },
          onError: (error) => {
            setIsLoading(false)
            errorHandler(error, t)
          },
        },
      )
    } else {
      loginByPhone.mutate(
        {
          phone: normalizeMobile(identifier),
          otp,
          code,
        },
        {
          onSuccess: async (res) => {
            if (res.data?.accessToken && res.data?.refreshToken) {
              // Refetch feature flags after successful login
              featureFlagsQuery.refetch()
              if (featureFlagsQuery.data?.flags) {
                setFlags(featureFlagsQuery.data.flags)
              }
              trackEvent({
                name: EAnalyticsEventName.Login,
                properties: {
                  user_id_custom: res.data.userID,
                },
              })

              // Clear redirecting URL and redirect to saved URL or dashboard
              Cookie.clearRedirectingUrl()
              const targetUrl = redirectingUrl ?? '/dashboard'
              // Use window.location.replace to redirect and clear history
              if (typeof window !== 'undefined') {
                window.location.replace(targetUrl)
              } else {
                replace(targetUrl)
              }
            }
          },
          onError: (error) => {
            setIsLoading(false)
            errorHandler(error, t)
          },
        },
      )
    }
  }

  return (
    <Card className='p-xl rounded-sm'>
      <Col className='gap-3xl'>
        <Button
          startIcon={<Icon name='ArrowRightLine' />}
          variant='transparent'
          size='xs'
          onClick={handleBack}
          className='gap-2xs'
        >
          {t('common_back')}
        </Button>
        <Otp
          mobileOrEmail={identifier}
          onResend={handleResend}
          onComplete={handleOtpComplete}
          onEdit={handleEdit}
          focusRef={submitRef}
          resendLoading={resendOtpMutation.isPending}
          resendError={resendOtpMutation.isError}
        />
        <Button
          ref={submitRef}
          size='lg'
          className='w-full'
          onClick={() => handleSubmit()}
          disabled={otpCode.length < OTP_LENGTH}
          loading={
            isLoading ||
            (isEmail ? loginByEmail.isPending : loginByPhone.isPending)
          }
        >
          {t('common_login')}
        </Button>
      </Col>
    </Card>
  )
}
